<template>
  <div class="ksk-mods" :class="{ 'ksk-mods--stacked': stacked }">
    <template v-for="(group, groupIndex) in groups" :key="groupIndex">
      <span
        v-for="(option, optionIndex) in group.items"
        :key="groupIndex + '-' + optionIndex"
        class="ksk-mods__item"
        :class="{ 'ksk-mods__item--na': option.isCanGet == 1 }"
      >
        <span class="ksk-mods__name">{{ tName(option.item_name) }}</span>
        <span v-if="(option.quantity || 1) > 1" class="ksk-mods__qty">× {{ option.quantity }}</span>
        <span v-if="showBcrs && parseInt(option.eligible_bcrs) === 1" class="ksk-mods__bcrs">BCRS</span>
        <span v-if="price(option)" class="ksk-mods__price">{{ priceText(option) }}</span>
        <span v-if="option.isCanGet == 1" class="ksk-mods__na">{{ $t("kiosk.common.notAvailable") }}</span>
        <span
          v-if="option.item_in_mog && hasSubItems(option.item_in_mog.modifier_group)"
          class="ksk-mods__sub"
        >
          <template v-for="(subGroup, subGroupIndex) in option.item_in_mog.modifier_group" :key="subGroupIndex">
            <span
              v-for="(subOption, subOptionIndex) in subGroup.items"
              :key="subGroupIndex + '-' + subOptionIndex"
              class="ksk-mods__item"
            >
              <span class="ksk-mods__name">{{ tName(subOption.item_name) }}</span>
              <span v-if="(subOption.quantity || 1) > 1" class="ksk-mods__qty">× {{ subOption.quantity }}</span>
              <span v-if="showBcrs && parseInt(subOption.eligible_bcrs) === 1" class="ksk-mods__bcrs">BCRS</span>
              <span v-if="price(subOption)" class="ksk-mods__price">{{ priceText(subOption) }}</span>
            </span>
          </template>
        </span>
      </span>
    </template>
  </div>
</template>

<script>
// KSK010 — checkout line-item modifiers as one caption line
// ("Large · Garlic dip ($ 1.00) · Sauce (No onion)"). Display only: the price
// text comes from the parent's formatter so the branch price toggle still applies.
import { translatedName } from "@/i18n/menuTranslate";

export default {
  name: "KskCartModifiers",
  props: {
    groups: { type: Array, default: () => [] },
    showBcrs: { type: [Boolean, Number], default: false },
    price: { type: Function, default: () => "" },
    // KSK052: one modifier per line (checkout) instead of a "·" caption line.
    // Each line: name (× qty) "+$ price" (KSK065: beside the name); nested options on
    // their own indented lines.
    stacked: { type: Boolean, default: false },
  },
  methods: {
    tName(source) {
      return translatedName(source);
    },
    // Stacked lines read as add-ons: "+$ 1.50" (a "x $ 1.50" per-unit price
    // after a quantity stays as is).
    priceText(option) {
      let text = String(this.price(option) || "");
      if (!this.stacked) return text;
      // Checkout's formatter wraps the price as "($ 1.00)" for the inline
      // caption; a stacked line shows it bare, as "+$ 1.00".
      text = text.replace(/^\((.*)\)$/, "$1");
      return text.startsWith("x") ? text : "+" + text;
    },
    hasSubItems(subGroups) {
      return (subGroups || []).some((g) => g.items && g.items.length);
    },
  },
};
</script>

<style scoped>
.ksk-mods {
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
  color: var(--ksk-text-secondary);
}
.ksk-mods__item {
  display: inline;
}
.ksk-mods__item + .ksk-mods__item::before {
  content: "·";
  margin: 0 8px;
  color: var(--ksk-text-secondary);
  font-weight: 700;
}
/* Stacked: one row per option, price right after the name (KSK065), nested
   options as indented rows (no "( … )"). KSK083: block + inline so a long
   name wraps and the price follows its last word instead of dropping to a
   line of its own. */
.ksk-mods--stacked .ksk-mods__item {
  display: block;
}
.ksk-mods--stacked .ksk-mods__item + .ksk-mods__item::before {
  content: none;
}
.ksk-mods--stacked .ksk-mods__name {
  overflow-wrap: anywhere;
}
.ksk-mods--stacked .ksk-mods__price {
  margin-left: var(--ksk-space-1);
  font-weight: 500;
  color: var(--ksk-text-secondary);
  white-space: nowrap;
}
.ksk-mods--stacked .ksk-mods__sub {
  display: block;
  margin-left: 0;
  padding-left: var(--ksk-space-2);
}
.ksk-mods--stacked .ksk-mods__sub::before,
.ksk-mods--stacked .ksk-mods__sub::after {
  content: none;
}
.ksk-mods__qty,
.ksk-mods__price,
.ksk-mods__bcrs,
.ksk-mods__na {
  margin-left: 6px;
}
.ksk-mods__qty,
.ksk-mods__price {
  font-weight: 600;
  color: var(--ksk-text);
  font-variant-numeric: tabular-nums;
}
.ksk-mods__bcrs {
  display: inline-block;
  padding: 0 6px;
  border-radius: 6px;
  background: var(--ksk-primary-container);
  color: var(--ksk-primary);
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  vertical-align: 1px;
}
.ksk-mods__item--na > .ksk-mods__name {
  opacity: 0.4;
}
.ksk-mods__na {
  color: var(--ksk-error);
  font-weight: 600;
}
.ksk-mods__sub {
  margin-left: 6px;
}
.ksk-mods__sub::before {
  content: "(";
}
.ksk-mods__sub::after {
  content: ")";
}
</style>
